import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import {
  CurrencyFilter,
  FinancialPriceLevelName,
  WorkspaceFinancialsDoc,
  WorkspaceFinancialsPriceLevelFragment,
} from "operations";

import { asMaybeNumber } from "lib/conversions";
import { ExtendedCost } from "./ExtendedCost";
import { ExtendedRetail } from "./ExtendedRetail";
import { Margin } from "./Margin";
import { FinancialColumn, SummaryBarContent } from "./styledComponents";
import { useFlags } from "launchdarkly-react-client-sdk";

type PriceLevel = Omit<WorkspaceFinancialsPriceLevelFragment, "id">;

export const getPriceLevelRollup = (mappedPriceLevels: any) => {
  const mergeValue = (acc: any, pl: any) => {
    return Object.keys({ ...acc, ...pl }).reduce((priceLevel, key) => {
      if (key === "extCost" || key === "extRetail") {
        priceLevel[key] =
          (asMaybeNumber(acc[key]) ?? 0) + (asMaybeNumber(pl[key]) ?? 0);
      } else {
        priceLevel[key] = acc[key] || pl[key];
      }
      return priceLevel;
    }, {} as any);
  };

  const reducer = (map: Map<string, PriceLevel>, pl: PriceLevel) => {
    const key = pl.currencyCode;
    const previous = map.get(key);
    const merged = mergeValue(previous || {}, pl);
    map.set(key, merged);
    return map;
  };

  const priceLevelRollup = Array.from(
    mappedPriceLevels.reduce(reducer, new Map()).values()
  );

  return priceLevelRollup as PriceLevel[];
};

const retailerPriceLevels = (data: any, type: "final" | "modified") =>
  data
    ?.filter((details: any) => !details.isBrandCurrency)
    .map((details: any) => {
      const currencyCode = details.currencyCode;
      const finalPriceLevel = (priceLevel: any) =>
        priceLevel.name === FinancialPriceLevelName.Final;
      const modifiedPriceLevel = (priceLevel: any) =>
        priceLevel.name === FinancialPriceLevelName.Modified;
      const priceLevelFunc =
        type === "final" ? finalPriceLevel : modifiedPriceLevel;
      const extCost =
        details.priceLevels.find(priceLevelFunc).extendedCost.value;
      const extRetail =
        details.priceLevels.find(priceLevelFunc).extendedRetail.value;
      return (
        {
          currencyCode,
          extCost,
          extRetail,
        } ?? []
      );
    });

const getBrandPriceLevels = (priceLevels: PriceLevel[]) =>
  priceLevels?.filter(
    (data) =>
      data.currencySource === CurrencyFilter.LinesheetCurrency ||
      data.currencySource === CurrencyFilter.LinesheetWholesaleCurrency ||
      data.currencySource === CurrencyFilter.LinesheetRetailCurrency
  );

const useRollup = (id: string, isBrand: boolean) => {
  const { data, loading } = useQuery(WorkspaceFinancialsDoc, {
    variables: { workspaceId: id },
  });
  const { finalRollup, modifiedRollup } = useMemo(() => {
    const assortments = data?.retailWorkspace?.assortments;

    if (assortments) {
      if (isBrand) {
        const mappedFinalPriceLevels = assortments.flatMap((a) =>
          getBrandPriceLevels(a.financials.total.final)
        );
        const mappedModifiedPriceLevels = assortments.flatMap((a) =>
          getBrandPriceLevels(a.financials.total.modified)
        );

        return {
          finalRollup: getPriceLevelRollup(mappedFinalPriceLevels),
          modifiedRollup: getPriceLevelRollup(mappedModifiedPriceLevels),
        };
      } else {
        const mappedFinalPriceLevels = retailerPriceLevels(
          assortments.flatMap((a) => a.financialOverview.financialDetails),
          "final"
        );

        const mappedModifiedPriceLevels = retailerPriceLevels(
          assortments.flatMap((a) => a.financialOverview.financialDetails),
          "modified"
        );
        return {
          finalRollup: getPriceLevelRollup(mappedFinalPriceLevels),
          modifiedRollup: getPriceLevelRollup(mappedModifiedPriceLevels),
        };
      }
    }
    return {
      finalRollup: undefined,
      modifiedRollup: undefined,
    };
  }, [data, isBrand]);
  return { loading, finalRollup, modifiedRollup };
};

export const Financials = ({
  workspaceId,
  moreCurrencies,
  retailerFinancials,
  showBrandCurrencies,
}: {
  workspaceId: string;
  moreCurrencies: boolean;
  retailerFinancials: boolean;
  showBrandCurrencies: boolean;
}) => {
  const { financialsUseAlternativeRules } = useFlags();

  const {
    loading: brandLoading,
    finalRollup: brandFinalRollup,
    modifiedRollup: brandModifiedRollup,
  } = useRollup(workspaceId, true);
  const {
    loading: retailerLoading,
    finalRollup: retailerFinalRollup,
    modifiedRollup: retailerModifiedRollup,
  } = useRollup(workspaceId, false);

  return (
    <SummaryBarContent>
      <FinancialColumn>
        {showBrandCurrencies && (
          <ExtendedCost
            loading={brandLoading}
            rollup={brandFinalRollup}
            moreCurrencies={moreCurrencies}
          />
        )}
        {retailerFinancials && (
          <ExtendedCost
            loading={retailerLoading}
            rollup={retailerFinalRollup}
            moreCurrencies={moreCurrencies}
          />
        )}
      </FinancialColumn>
      <FinancialColumn>
        {showBrandCurrencies && (
          <ExtendedRetail
            loading={brandLoading}
            rollup={brandFinalRollup}
            moreCurrencies={moreCurrencies}
          />
        )}
        {retailerFinancials && (
          <ExtendedRetail
            loading={retailerLoading}
            rollup={retailerFinalRollup}
            moreCurrencies={moreCurrencies}
          />
        )}
      </FinancialColumn>
      <FinancialColumn>
        {showBrandCurrencies && (
          <Margin
            loading={brandLoading}
            rollup={
              financialsUseAlternativeRules
                ? brandModifiedRollup
                : brandFinalRollup
            }
            moreCurrencies={moreCurrencies}
          />
        )}
        {retailerFinancials && (
          <Margin
            loading={retailerLoading}
            rollup={
              financialsUseAlternativeRules
                ? retailerModifiedRollup
                : retailerFinalRollup
            }
            moreCurrencies={moreCurrencies}
          />
        )}
      </FinancialColumn>
    </SummaryBarContent>
  );
};
